import { Pressable, View } from 'react-native';

import { cn } from '@/components/ui/cn';
import { Text } from '@/components/ui/Text';
import { type TemplateRole } from '@rbf/shared';

/** Assignable roles, low → high, with the copy shown throughout the share UI. */
export const ROLE_ORDER: TemplateRole[] = ['member', 'editor', 'admin'];

export const ROLE_META: Record<TemplateRole, { label: string; description: string }> = {
  member: {
    label: 'Member',
    description: 'Can run templates and view history.',
  },
  editor: {
    label: 'Editor',
    description: 'Can also edit the template’s steps.',
  },
  admin: {
    label: 'Admin',
    description: 'Can also manage sharing and people.',
  },
};

export interface RolePickerProps {
  value: TemplateRole;
  onChange: (role: TemplateRole) => void;
  disabled?: boolean;
  /** Compact segmented control (labels only), for inline roster edits. */
  compact?: boolean;
}

/** Segmented member / editor / admin selector. */
export function RolePicker({ value, onChange, disabled, compact }: RolePickerProps) {
  return (
    <View className="gap-1.5">
      <View className="flex-row gap-1 rounded-xl bg-well p-1">
        {ROLE_ORDER.map((role) => {
          const selected = role === value;
          return (
            <Pressable
              key={role}
              disabled={disabled}
              onPress={() => onChange(role)}
              className={cn(
                'flex-1 items-center rounded-lg py-1.5',
                selected ? 'bg-card' : 'bg-transparent',
                disabled ? 'opacity-50' : null,
              )}
            >
              <Text variant={selected ? 'bodySemibold' : 'body'}>
                {ROLE_META[role].label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      {compact ? null : (
        <Text variant="caption" className="px-1 text-faint">
          {ROLE_META[value].description}
        </Text>
      )}
    </View>
  );
}
